/**
 * Application shell.
 *
 * First load shows the laboratory immediately (spec 61): the chain, the
 * library and the transport are all present and usable before any file exists.
 * There is no landing page and no marketing panel.
 */

import { useEffect, useState } from 'react';
import { useApp } from './stores/appStore';
import { engine } from './audio/engine';
import { Analyzer } from './ui/components/Analyzer';
import { ChainGraph } from './ui/components/ChainGraph';
import { HardwareLibrary } from './ui/components/HardwareLibrary';
import { Inspector } from './ui/components/Inspector';
import { LeftPanel } from './ui/components/LeftPanel';
import { TopBar } from './ui/components/TopBar';
import { Transport } from './ui/components/Transport';
import { Panels } from './ui/panels/Panels';
import { Kbd, Note, Spinner } from './ui/components/primitives';

export default function App() {
  const boot = useApp((s) => s.boot);
  const engineReady = useApp((s) => s.engineReady);
  const engineError = useApp((s) => s.engineError);
  const booting = useApp((s) => s.booting);
  const buffer = useApp((s) => s.buffer);
  const loadFile = useApp((s) => s.loadFile);
  const librarySlot = useApp((s) => s.librarySlot);
  const setPanel = useApp((s) => s.setPanel);
  const openPanel = useApp((s) => s.openPanel);

  const [dragging, setDragging] = useState(false);
  const [showLeft, setShowLeft] = useState(true);
  const [showRight, setShowRight] = useState(true);

  useEffect(() => {
    void boot();
  }, [boot]);

  // Browsers require a user gesture before audio can start.
  useEffect(() => {
    const resume = () => void engine.resume();
    window.addEventListener('pointerdown', resume);
    window.addEventListener('keydown', resume);
    return () => {
      window.removeEventListener('pointerdown', resume);
      window.removeEventListener('keydown', resume);
    };
  }, []);

  // ---- Drag and drop ------------------------------------------------------
  useEffect(() => {
    let depth = 0;
    const onOver = (e: DragEvent) => {
      e.preventDefault();
    };
    const onEnter = (e: DragEvent) => {
      e.preventDefault();
      depth++;
      if (e.dataTransfer?.types.includes('Files')) setDragging(true);
    };
    const onLeave = () => {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      depth = 0;
      setDragging(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) void loadFile(f).catch(() => undefined);
    };
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [loadFile]);

  // ---- Keyboard (spec 73, 74) --------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) {
        if (e.key === 'Escape') t.blur();
        return;
      }
      const s = useApp.getState();
      const meta = e.metaKey || e.ctrlKey;

      if (meta && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) s.redo();
        else s.undo();
        return;
      }
      if (meta && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        s.redo();
        return;
      }
      if (meta && e.key.toLowerCase() === 's') {
        e.preventDefault();
        s.setPanel('project');
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          s.togglePlay();
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          s.toggleAb();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          s.toggleMute();
          break;
        case 'b':
        case 'B':
          e.preventDefault();
          s.setBlind(!s.blind);
          break;
        case 'l':
        case 'L':
          e.preventDefault();
          s.setLoop(!s.looping);
          break;
        case 'x':
        case 'X':
          e.preventDefault();
          if (s.project.comparison === 'abx') s.startAbx();
          break;
        case 'Escape':
          e.preventDefault();
          if (s.librarySlot) s.closeLibrary();
          else if (s.openPanel) s.setPanel(null);
          else if (s.inspectorNode) s.setInspector(null);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          s.nudge(e.shiftKey ? -1 : -5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          s.nudge(e.shiftKey ? 1 : 5);
          break;
        case 'ArrowUp':
          e.preventDefault();
          s.setMasterGain(Math.min(0, s.project.engine.masterGainDb + (e.shiftKey ? 0.5 : 2)));
          break;
        case 'ArrowDown':
          e.preventDefault();
          s.setMasterGain(Math.max(-60, s.project.engine.masterGainDb - (e.shiftKey ? 0.5 : 2)));
          break;
        case '1':
        case '2':
        case '3':
        case '4':
          e.preventDefault();
          s.selectBank('dac', Number(e.key) - 1);
          break;
        case '?':
          e.preventDefault();
          s.setPanel('console');
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (engineError) {
    return (
      <div style={{ padding: 40, maxWidth: 720, margin: '0 auto' }}>
        <h1 style={{ fontSize: 16, letterSpacing: '0.14em', textTransform: 'uppercase' }}>DAC ENGINE cannot start</h1>
        <Note tone="danger">{engineError}</Note>
        <p className="dim small">
          The engine needs the Web Audio API with AudioWorklet support. Current versions of Chrome, Edge, Firefox and
          Safari all provide it; the most common cause of this message is an unusually locked-down browser
          configuration or a very old build.
        </p>
        <button className="btn primary" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }

  return (
    <div className={`app${showLeft ? '' : ' no-left'}${showRight ? '' : ' no-right'}`}>
      <div className="area-top">
        <TopBar onOpenPanel={setPanel} />
      </div>

      <div className="area-left">
        <LeftPanel />
      </div>

      <div className="area-center">
        {booting && !engineReady && (
          <div className="empty-state">
            <Spinner />
            <h2>Starting the engine</h2>
            <p className="dim">Loading DSP modules and indexing the hardware database.</p>
          </div>
        )}

        {engineReady && !buffer && <FirstRun />}

        {engineReady && <ChainGraph />}
      </div>

      <div className="area-right">
        <Inspector />
      </div>

      <div className="area-analyzer">
        <Analyzer />
      </div>

      <div className="area-transport">
        <Transport />
      </div>

      {librarySlot && <HardwareLibrary />}
      {openPanel && openPanel !== 'library' && <Panels />}

      {dragging && (
        <div className="dropzone">
          <h2>Drop to load</h2>
        </div>
      )}

      <button
        className="btn sm"
        style={{ position: 'fixed', left: 6, bottom: 64, zIndex: 30, opacity: 0.65 }}
        onClick={() => setShowLeft((v) => !v)}
        title="Toggle the control rail"
      >
        {showLeft ? '◀' : '▶'}
      </button>
      <button
        className="btn sm"
        style={{ position: 'fixed', right: 6, bottom: 64, zIndex: 30, opacity: 0.65 }}
        onClick={() => setShowRight((v) => !v)}
        title="Toggle the inspector"
      >
        {showRight ? '▶' : '◀'}
      </button>
    </div>
  );
}

function FirstRun() {
  const profileFor = useApp((s) => s.profileFor);
  const dac = profileFor('A', 'dac');
  const amp = profileFor('A', 'amp');
  const out = profileFor('A', 'output');

  return (
    <div className="empty-state">
      <h2>Drop an audio file to begin</h2>
      <p>
        FLAC, WAV, AIFF, and whatever else this browser can decode. The file is read locally and never uploaded. The
        chain below is already live: choose hardware now if you like, then drop music and swap components while it
        plays.
      </p>
      <div className="row" style={{ gap: 14, fontSize: 11 }}>
        <span className="dim">
          Default chain: <b className="bright">{dac?.model ?? '--'}</b> → <b className="bright">{amp?.model ?? '--'}</b> →{' '}
          <b className="bright">{out?.model ?? '--'}</b>
        </span>
      </div>
      <div className="row wrap" style={{ gap: 10, marginTop: 4, justifyContent: 'center' }}>
        <span className="faint tiny">
          <Kbd>Space</Kbd> play
        </span>
        <span className="faint tiny">
          <Kbd>A</Kbd> A/B
        </span>
        <span className="faint tiny">
          <Kbd>B</Kbd> blind
        </span>
        <span className="faint tiny">
          <Kbd>M</Kbd> mute
        </span>
        <span className="faint tiny">
          <Kbd>1</Kbd>–<Kbd>4</Kbd> banks
        </span>
        <span className="faint tiny">
          <Kbd>←</Kbd>
          <Kbd>→</Kbd> seek
        </span>
        <span className="faint tiny">
          <Kbd>Esc</Kbd> close
        </span>
      </div>
    </div>
  );
}
